import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import {
  useGetCardTransactions,
  useGetDeposits,
  useHasSwaps,
} from "@/hooks/use-queries";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";
import SwapTab from "../_misc/swap/swap-tab";
import PillTabsTrigger from "@/components/pill-tabs-trigger";
import { useTabParam } from "@/hooks/use-tab-param";

const PAGE_SIZE = 10;

type TransactionTab = "wallet" | "cards" | "swap";

const Transactions = () => {
  const hasSwaps = useHasSwaps();
  const [tab, setTab] = useTabParam<TransactionTab>(
    "tab",
    "wallet",
    hasSwaps ? ["wallet", "cards", "swap"] : ["wallet", "cards"],
  );

  // ── Wallet: server-paginated ────────────────────────────────────────────
  const [walletPagination, setWalletPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits({
      page: walletPagination.pageIndex + 1,
      limit: walletPagination.pageSize,
    });
  const walletPageCount = transactions?.totalPages || 1;

  // ── Cards: fetched in one batch, paginated on the client ────────────────
  const [cardPagination, setCardPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });
  const cardRows = cardTransactions?.data ?? [];
  const cardPageCount = Math.max(1, Math.ceil(cardRows.length / PAGE_SIZE));
  const cardPageRows = cardRows.slice(
    cardPagination.pageIndex * PAGE_SIZE,
    (cardPagination.pageIndex + 1) * PAGE_SIZE,
  );

  const handleWalletPageChange = (page: number) => {
    if (page > 0 && page <= walletPageCount) {
      setWalletPagination((p) => ({ ...p, pageIndex: page - 1 }));
    }
  };

  const handleCardPageChange = (page: number) => {
    if (page > 0 && page <= cardPageCount) {
      setCardPagination((p) => ({ ...p, pageIndex: page - 1 }));
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Keep the active page in range if the row count shrinks under it. Guard on a
  // loaded response — while react-query refetches an uncached page, `data` is
  // briefly undefined and `totalPages` would read as 1, bouncing us to page 1.
  useEffect(() => {
    if (transactions?.totalPages) {
      setWalletPagination((p) => ({
        ...p,
        pageIndex: Math.min(p.pageIndex, walletPageCount - 1),
      }));
    }
  }, [transactions?.totalPages, walletPageCount]);

  useEffect(() => {
    setCardPagination((p) => ({
      ...p,
      pageIndex: Math.min(p.pageIndex, cardPageCount - 1),
    }));
  }, [cardPageCount]);

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value) => setTab(value as TransactionTab)}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="border-dark-stroke-3 h-10 w-fit rounded-full border bg-dark-grey-50 p-1">
          <PillTabsTrigger value="wallet" active={tab === "wallet"}>
            Wallet
          </PillTabsTrigger>
          <PillTabsTrigger value="cards" active={tab === "cards"}>
            Cards
          </PillTabsTrigger>
          {hasSwaps && (
            <PillTabsTrigger value="swap" active={tab === "swap"}>
              Swap
            </PillTabsTrigger>
          )}
        </TabsList>
        <div className="bg-dark-card-3 text-dark-text-400 mt-4 rounded-2xl p-4 backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={transactions?.data ?? []}
                isLoading={isLoadingTransactions}
                pagination={walletPagination}
                setPagination={setWalletPagination}
                pageCount={walletPageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingTransactions}
                transactions={transactions?.data ?? []}
                currentPage={walletPagination.pageIndex + 1}
                totalPages={walletPageCount}
                handlePageChange={handleWalletPageChange}
              />
            </div>
          </TabsContent>
          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardPageRows}
                isLoading={isLoadingCardTransactions}
                pagination={cardPagination}
                setPagination={setCardPagination}
                pageCount={cardPageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={cardPageRows}
                type="cards"
                currentPage={cardPagination.pageIndex + 1}
                totalPages={cardPageCount}
                handlePageChange={handleCardPageChange}
              />
            </div>
          </TabsContent>

          {hasSwaps && (
            <TabsContent value="swap" className="mt-2">
              <SwapTab />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
